import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  GitHub as GitHubIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { AssignedProjects } from '../../components/developer';
import type { AssignedProject } from '../../components/developer/AssignedProjects';
import { DeveloperDashboardService, DeveloperProject } from '../../services/DeveloperDashboardService';
import { exportSpecificationsToPDF, ProjectSpecification } from '../../utils/pdfExport';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`projects-tabpanel-${index}`}
      aria-labelledby={`projects-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Developer Projects Page
 * View and manage assigned projects with files and documentation
 */
const DeveloperProjectsPage: React.FC = () => {
  console.log('📁 DeveloperProjectsPage MOUNTED - VERSION 2.0 WITH URL ROUTING 📁');
  
  const { '*': urlPath } = useParams();
  const navigate = useNavigate();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [projects, setProjects] = useState<DeveloperProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<DeveloperProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [specifications, setSpecifications] = useState<{
    constitution?: string;
    specification?: string;
    technicalPlan?: string;
    tasks?: string;
  } | null>(null);
  const [specsLoading, setSpecsLoading] = useState(false);

  // Load projects on mount
  useEffect(() => {
    console.log('📁 DeveloperProjectsPage - Loading projects...');
    loadProjects();
  }, []);
  
  // Handle URL-based project selection
  useEffect(() => {
    if (!urlPath || projects.length === 0) return;
    
    console.log('🔗 URL path changed:', urlPath);
    
    // Parse project ID from URL (e.g., "14" or "14/spec")
    const projectIdMatch = urlPath.match(/^(\d+)/);
    if (projectIdMatch) {
      const projectId = parseInt(projectIdMatch[1], 10);
      const project = projects.find(p => p.id === projectId);
      
      if (project) {
        console.log('✅ Found project from URL:', project.name);
        setSelectedProject(project);
        
        // Check if we should show specifications tab
        if (urlPath.includes('/spec')) {
          console.log('📄 URL indicates specifications view, loading specs...');
          handleViewSpec(projectId);
        }
      }
    }
  }, [urlPath, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📡 Fetching projects from API...');
      const data = await DeveloperDashboardService.getProjects();
      console.log('✅ Projects loaded:', {
        count: data.length,
        projects: data.map(p => ({ id: p.id, name: p.name, tasksTotal: p.tasksTotal }))
      });
      setProjects(data);
      if (data.length > 0) {
        setSelectedProject(data[0]);
        console.log('📌 Selected first project:', data[0].name);
      } else {
        console.warn('⚠️ No projects found for this developer');
      }
    } catch (err) {
      console.error('❌ Failed to load projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Convert DeveloperProject to AssignedProject
  const convertToAssignedProjects = (): AssignedProject[] => {
    return projects.map((project) => ({
      id: project.id,
      name: project.name,
      client: project.client || 'Unknown Client',
      status: project.status,
      progress: project.progress,
      technologies: project.technologies || [],
      tasksTotal: project.tasksTotal || 0,
      tasksCompleted: project.tasksCompleted || 0,
      projectManager: project.projectManager || undefined,
      startDate: project.startDate || undefined,
      endDate: project.endDate || undefined,
      githubRepo: project.githubRepo || undefined,
      githubBranch: project.githubBranch || 'main',
      openPRs: project.openPRs || undefined,
      openIssues: project.openIssues || undefined,
      lastCommit: project.lastCommit || undefined,
      description: project.description || undefined,
    }));
  };

  const handleViewSpec = async (projectId: number) => {
    try {
      console.log('📄 handleViewSpec called for project:', projectId);
      setSpecsLoading(true);
      setError(null);
      
      // Navigate to project with spec view
      navigate(`/dashboard/developer/projects/${projectId}/spec`);
      
      const specs = await DeveloperDashboardService.getProjectSpecifications(projectId);
      setSpecifications(specs);
      setCurrentTab(2); // Switch to documentation tab
      console.log('✅ Specifications loaded successfully');
    } catch (err) {
      console.error('❌ Error loading specifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load specifications');
    } finally {
      setSpecsLoading(false);
    }
  };

  const handleOpenGitHub = (repo: string) => {
    const url = repo.startsWith('http') ? repo : `https://github.com/${repo}`;
    window.open(url, '_blank');
  };

  const handleProjectSelect = (project: DeveloperProject) => {
    console.log('📂 Project selected:', project.name, 'ID:', project.id);
    setSelectedProject(project);
    // Update URL to reflect selected project
    navigate(`/dashboard/developer/projects/${project.id}`);
    setCurrentTab(1); // Switch to files tab
  };

  const handleExportPDF = () => {
    if (!selectedProject || !specifications) {
      alert('Please load specifications first before exporting to PDF');
      return;
    }

    try {
      const pdfSpec: ProjectSpecification = {
        projectName: selectedProject.name,
        clientName: selectedProject.client || 'Unknown Client',
        constitution: specifications.constitution,
        specification: specifications.specification,
        technicalPlan: specifications.technicalPlan,
        tasks: specifications.tasks,
        generatedDate: new Date().toLocaleDateString(),
      };

      exportSpecificationsToPDF(pdfSpec);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Projects
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        View and manage your assigned development projects
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="project views"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Projects" />
          <Tab label="Project Files" />
          <Tab label="Documentation" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Projects you're currently working on with GitHub integration
          </Typography>
          <AssignedProjects
            projects={convertToAssignedProjects()}
            onViewSpec={handleViewSpec}
            onOpenGitHub={handleOpenGitHub}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Project Files
          </Typography>
          
          {!selectedProject ? (
            <Alert severity="info">Select a project from the "All Projects" tab to view files</Alert>
          ) : (
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedProject.name}
                  </Typography>
                  <Chip label={selectedProject.status} size="small" sx={{ mr: 1 }} />
                  {/* TODO: Add GitHub repo info to DeveloperProject */}
                  <Chip 
                    icon={<GitHubIcon />}
                    label="View on GitHub" 
                    size="small" 
                    clickable
                    disabled
                  />
                </CardContent>
              </Card>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Project file browser coming soon. For now, access files via GitHub:
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Source Code"
                    secondary="Application source files, components, and modules"
                  />
                  <Button 
                    size="small" 
                    endIcon={<OpenInNewIcon />}
                    onClick={() => selectedProject.githubRepo && handleOpenGitHub(selectedProject.githubRepo)}
                  >
                    View on GitHub
                  </Button>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Configuration"
                    secondary="Environment configs, build settings, deployment files"
                  />
                  <Button 
                    size="small" 
                    endIcon={<OpenInNewIcon />}
                    onClick={() => selectedProject.githubRepo && handleOpenGitHub(selectedProject.githubRepo)}
                  >
                    View on GitHub
                  </Button>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Documentation"
                    secondary="README, API docs, technical specifications"
                  />
                  <Button 
                    size="small" 
                    endIcon={<OpenInNewIcon />}
                    onClick={() => selectedProject.githubRepo && handleOpenGitHub(`${selectedProject.githubRepo}/tree/main/docs`)}
                  >
                    View on GitHub
                  </Button>
                </ListItem>
              </List>

              {/* TODO: Add file tree browser
              <Box sx={{ mt: 2 }}>
                - Tree view of project files
                - File preview/viewer
                - Download files
                - File search
              </Box>
              */}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Project Documentation
            </Typography>
            {selectedProject && specifications && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                sx={{ ml: 2 }}
              >
                Export to PDF
              </Button>
            )}
          </Box>
          
          {!selectedProject ? (
            <Alert severity="info">Select a project from the "All Projects" tab to view documentation</Alert>
          ) : (
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedProject.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProject.description || 'No description available'}
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📜 Constitution
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Project principles, success criteria, and core values
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => handleViewSpec(selectedProject.id)}>
                        View Constitution
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        🏗️ Technical Specification
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Features, user stories, and functional requirements
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => handleViewSpec(selectedProject.id)}>
                        View Specification
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        💻 Implementation Plan
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Architecture, tech stack, and development roadmap
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => handleViewSpec(selectedProject.id)}>
                        View Plan
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        ✅ Task Breakdown
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Detailed tasks with estimates and dependencies
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => handleViewSpec(selectedProject.id)}>
                        View Tasks
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📚 README & API Docs
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Setup instructions, API documentation, and developer guides
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        endIcon={<OpenInNewIcon />}
                        onClick={() => selectedProject.githubRepo && handleOpenGitHub(`${selectedProject.githubRepo}#readme`)}
                      >
                        View on GitHub
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              </Grid>

              {specsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {specifications && (
                <Box sx={{ mt: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Loaded Specifications
                      </Typography>
                      
                      {specifications.constitution && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            📜 Constitution
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                            {specifications.constitution}
                          </Typography>
                        </Box>
                      )}
                      
                      {specifications.specification && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            🏗️ Specification
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                            {specifications.specification}
                          </Typography>
                        </Box>
                      )}
                      
                      {specifications.technicalPlan && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            💻 Technical Plan
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                            {specifications.technicalPlan}
                          </Typography>
                        </Box>
                      )}
                      
                      {specifications.tasks && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            ✅ Tasks
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                            {specifications.tasks}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default DeveloperProjectsPage;
